/**
 * Pipeline Canvas - React Flow editor
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Connection,
  useNodesState,
  useEdgesState,
  addEdge,
  Controls,
  Background,
  Panel,
  BackgroundVariant,
  MiniMap,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { TaskNode } from './nodes/TaskNode';
import { ConditionalNode } from './nodes/ConditionalNode';
import { EdgeRoute, EdgeRouteContext, MovableEdge } from './edges/MovableEdge';
import { DEFAULT_CREATE_ITEM_FIELD_MAPPINGS } from './CreateItemConfig';
import { useAgents } from '../hooks/useAgents';
import type { PipelineDefinition, PipelineEdge, PipelineNode, TaskTypeInfo } from '../types/pipeline';
import { CanvasLayout, loadCanvasLayout, saveCanvasLayout, stableEdgeKey, stableNodeKey } from '../utils/canvasLayout';
import './PipelineCanvas.css';

const nodeTypes = {
  taskNode: TaskNode,
  conditionalNode: ConditionalNode,
};

const edgeTypes = {
  movable: MovableEdge,
};

// Saved position for a node, falling back to its raw id (layouts saved before
// stable keys existed) and then to the backend's auto-layout position.
function layoutPosition(node: PipelineNode, layout: CanvasLayout) {
  return layout.nodes[stableNodeKey(node)] ?? layout.nodes[node.id] ?? node.position;
}

function edgeLayoutKey(edge: Pick<PipelineEdge, 'source' | 'target' | 'source_handle'>, nodesById: Map<string, PipelineNode>) {
  const keyOf = (id: string) => {
    const node = nodesById.get(id);
    return node ? stableNodeKey(node) : id;
  };
  return stableEdgeKey(keyOf(edge.source), keyOf(edge.target), edge.source_handle);
}

interface PipelineCanvasProps {
  pipeline: PipelineDefinition | null;
  onPipelineChange: (pipeline: PipelineDefinition) => void;
  onNodeClick?: (node: any) => void;
  onEdgeClick?: (edge: any) => void;
  readonly?: boolean;
  // Execution status per pipeline node id (raw n.id, not the sanitized backend
  // step_name - callers reconcile that when building this map). Threaded into
  // node data.status, which TaskNode/ConditionalNode already render. Matches
  // their data.status literal union exactly (not the ExecutionStatus enum,
  // which also has a 'cancelled' member these node components don't render).
  nodeStatuses?: Record<string, 'pending' | 'running' | 'completed' | 'failed'>;
  // Fired on single-click alongside onNodeClick (which opens the config editor) -
  // used to drive the execution detail panel.
  onNodeSelect?: (nodeId: string) => void;
  // Bump this (e.g. a counter) to force node badges to pick up an agent
  // edited via the Agent Panel - see useAgents' refreshKey.
  agentsRefreshKey?: unknown;
}

const FlowContent: React.FC<PipelineCanvasProps> = ({
  pipeline,
  onPipelineChange,
  onNodeClick,
  onEdgeClick,
  readonly = false,
  nodeStatuses,
  onNodeSelect,
  agentsRefreshKey,
}) => {
  const reactFlowInstance = useReactFlow();
  const { agentsById } = useAgents(agentsRefreshKey);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeSelect?.(node.id);
      if (onNodeClick && pipeline) {
        // Find the pipeline node data
        const pipelineNode = pipeline.nodes.find(n => n.id === node.id);
        if (pipelineNode) {
          onNodeClick(pipelineNode);
        }
      }
    },
    [onNodeClick, onNodeSelect, pipeline]
  );

  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      if (onEdgeClick && pipeline) {
        // Find the pipeline edge data
        const pipelineEdge = pipeline.edges.find(e => e.id === edge.id);
        if (pipelineEdge) {
          onEdgeClick(pipelineEdge);
        }
      }
    },
    [onEdgeClick, pipeline]
  );

  const initialLayout = pipeline ? loadCanvasLayout(pipeline.id) : { nodes: {}, edges: {} };
  const initialNodesById = new Map((pipeline?.nodes ?? []).map(n => [n.id, n]));

  // Convert pipeline nodes to React Flow nodes
  const initialNodes: Node[] = pipeline?.nodes.map(n => {
    const agent = n.agent_id ? agentsById.get(n.agent_id) : undefined;
    const isConditional = n.task_type === 'branch';

    const position = layoutPosition(n, initialLayout);

    return {
      id: n.id,
      type: isConditional ? 'conditionalNode' : 'taskNode',
      position,
      data: isConditional ? {
        taskType: n.task_type,
        label: n.label,
        condition: n.config?.condition,
        status: nodeStatuses?.[n.id],
      } : {
        taskType: n.task_type,
        label: n.label,
        config: n.config,
        agentId: n.agent_id,
        agentVersion: n.agent_version,
        agentName: agent?.name,
        modelName: agent?.model?.model_name,
        contextFieldCount: agent?.context_field_count,
        status: nodeStatuses?.[n.id],
      },
    };
  }) || [];

  // Convert pipeline edges to React Flow edges
  const initialEdges: Edge[] = pipeline?.edges.map(e => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.source_handle ?? undefined,
    label: e.label,
    type: 'movable',
    animated: true,
    data: {
      route: initialLayout.edges[edgeLayoutKey(e, initialNodesById)],
    },
  })) || [];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync node set + data changes from pipeline (config updates from sidebar, or the
  // pipeline arriving asynchronously after this canvas already mounted with pipeline=null
  // - e.g. ExecutionMonitor's Graph tab, which fetches the pipeline after the first
  // render). Reconciles added nodes too, not just data patches on nodes that already
  // exist in local state - useNodesState only consumes initialNodes once, on mount.
  useEffect(() => {
    if (!pipeline) return;

    setNodes((currentNodes) => {
      const currentById = new Map(currentNodes.map(node => [node.id, node]));

      return pipeline.nodes.map(pipelineNode => {
        const isConditional = pipelineNode.task_type === 'branch';
        const agent = pipelineNode.agent_id ? agentsById.get(pipelineNode.agent_id) : undefined;
        const data = isConditional ? {
          taskType: pipelineNode.task_type,
          label: pipelineNode.label,
          condition: pipelineNode.config?.condition,
        } : {
          taskType: pipelineNode.task_type,
          label: pipelineNode.label,
          config: pipelineNode.config,
          agentId: pipelineNode.agent_id,
          agentVersion: pipelineNode.agent_version,
          agentName: agent?.name,
          modelName: agent?.model?.model_name,
          contextFieldCount: agent?.context_field_count,
        };

        const existing = currentById.get(pipelineNode.id);
        if (existing) {
          return { ...existing, data: { ...existing.data, ...data } };
        }

        // Brand new node not yet in local state - build it fresh, restoring its
        // saved position the same way initialNodes does on first mount.
        const position = layoutPosition(pipelineNode, loadCanvasLayout(pipeline.id));

        return {
          id: pipelineNode.id,
          type: isConditional ? 'conditionalNode' : 'taskNode',
          position,
          data: { ...data, status: nodeStatuses?.[pipelineNode.id] },
        };
      });
    });
  }, [pipeline?.nodes, setNodes, agentsById]);

  // Sync execution status onto nodes as it changes (2s poll from ExecutionMonitor),
  // patching only data.status without rebuilding the rest of each node's data.
  useEffect(() => {
    if (!nodeStatuses) return;

    setNodes((currentNodes) =>
      currentNodes.map(node => {
        const status = nodeStatuses[node.id];
        if (node.data.status !== status) {
          return { ...node, data: { ...node.data, status } };
        }
        return node;
      })
    );
  }, [nodeStatuses, setNodes]);

  // Sync edge set + data changes from pipeline - same reasoning as the node-sync
  // effect above: reconciles added edges, not just data patches on existing ones.
  useEffect(() => {
    if (!pipeline) return;

    setEdges((currentEdges) => {
      const currentById = new Map(currentEdges.map(edge => [edge.id, edge]));
      const nodesById = new Map(pipeline.nodes.map(n => [n.id, n]));
      const layout = loadCanvasLayout(pipeline.id);

      return pipeline.edges.map(pipelineEdge => {
        const patch = {
          label: pipelineEdge.label,
          sourceHandle: pipelineEdge.source_handle ?? undefined,
        };
        const data = {
          edge_type: pipelineEdge.edge_type,
          loop_config: pipelineEdge.loop_config,
        };

        const existing = currentById.get(pipelineEdge.id);
        if (existing) {
          // Merge so a segment dragged this session isn't wiped.
          return { ...existing, ...patch, data: { ...existing.data, ...data } };
        }

        return {
          id: pipelineEdge.id,
          source: pipelineEdge.source,
          target: pipelineEdge.target,
          type: 'movable',
          animated: true,
          ...patch,
          data: { ...data, route: layout.edges[edgeLayoutKey(pipelineEdge, nodesById)] },
        };
      });
    });
  }, [pipeline?.edges, pipeline?.nodes, setEdges]);

  // Latest pipeline for the route setter below, so it stays stable across
  // pipeline changes and MovableEdge's drag listeners don't go stale mid-drag.
  const pipelineRef = useRef(pipeline);
  pipelineRef.current = pipeline;

  const setEdgeRoute = useCallback(
    (edgeId: string, route: EdgeRoute | null) => {
      setEdges((eds) => eds.map(edge =>
        edge.id === edgeId ? { ...edge, data: { ...edge.data, route: route ?? undefined } } : edge
      ));

      const current = pipelineRef.current;
      const pipelineEdge = current?.edges.find(e => e.id === edgeId);
      if (!current || !pipelineEdge) return;

      const layout = loadCanvasLayout(current.id);
      const key = edgeLayoutKey(pipelineEdge, new Map(current.nodes.map(n => [n.id, n])));
      if (route) {
        layout.edges[key] = route;
      } else {
        delete layout.edges[key];
      }
      saveCanvasLayout(current.id, layout);
    },
    [setEdges]
  );
  const edgeRouteSetter = useMemo(() => (readonly ? null : setEdgeRoute), [readonly, setEdgeRoute]);

  // Sync node changes back to pipeline
  const handleNodesChange = useCallback(
    (changes: any[]) => {
      onNodesChange(changes);

      if (!pipeline || readonly) return;

      // Check if this is a remove change
      const hasRemove = changes.some(change => change.type === 'remove');
      if (hasRemove) {
        console.log('Node deletion detected, syncing to pipeline...');
      }

      // After React Flow processes the changes, sync to pipeline
      setTimeout(() => {
        setNodes((currentNodes) => {
          const updatedPipelineNodes = currentNodes.map(node => {
            const isConditional = node.type === 'conditionalNode';

            if (isConditional) {
              return {
                id: node.id,
                task_type: 'branch',
                label: node.data.label,
                position: node.position,
                config: { condition: node.data.condition },
              };
            } else {
              return {
                id: node.id,
                task_type: node.data.taskType,
                label: node.data.label,
                position: node.position,
                config: node.data.config || {},
                agent_id: node.data.agentId,
                agent_version: node.data.agentVersion,
              };
            }
          });

          console.log(`Syncing nodes to pipeline: ${updatedPipelineNodes.length} nodes`);

          // Also clean up edges that reference deleted nodes
          setEdges((currentEdges) => {
            const nodeIds = new Set(currentNodes.map(n => n.id));
            const validEdges = currentEdges.filter(edge =>
              nodeIds.has(edge.source) && nodeIds.has(edge.target)
            );

            const updatedPipelineEdges = validEdges.map(edge => {
              // Find the corresponding pipeline edge to preserve its config
              const existingEdge = pipeline.edges.find(e => e.id === edge.id);
              return {
                id: edge.id,
                source: edge.source,
                target: edge.target,
                label: typeof edge.label === 'string' ? edge.label : undefined,
                edge_type: existingEdge?.edge_type,
                loop_config: existingEdge?.loop_config,
                source_handle: (edge.sourceHandle as 'true' | 'false' | null) ?? existingEdge?.source_handle,
              };
            });

            console.log(`Cleaned up edges: ${currentEdges.length} -> ${validEdges.length} edges`);

            onPipelineChange({
              ...pipeline,
              nodes: updatedPipelineNodes,
              edges: updatedPipelineEdges,
            });

            return validEdges;
          });

          return currentNodes;
        });
      }, 0);
    },
    [onNodesChange, pipeline, readonly, setNodes, setEdges, onPipelineChange]
  );

  // Sync edge changes back to pipeline
  const handleEdgesChange = useCallback(
    (changes: any[]) => {
      onEdgesChange(changes);

      if (!pipeline || readonly) return;

      // Check if this is a remove change
      const hasRemove = changes.some(change => change.type === 'remove');
      if (hasRemove) {
        console.log('Edge deletion detected, syncing to pipeline...');
      }

      // After React Flow processes the changes, sync to pipeline
      setTimeout(() => {
        setEdges((currentEdges) => {
          const updatedPipelineEdges = currentEdges.map(edge => {
            // Find the corresponding pipeline edge to preserve its config
            const existingEdge = pipeline.edges.find(e => e.id === edge.id);
            return {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              label: typeof edge.label === 'string' ? edge.label : undefined,
              edge_type: existingEdge?.edge_type,
              loop_config: existingEdge?.loop_config,
              source_handle: (edge.sourceHandle as 'true' | 'false' | null) ?? existingEdge?.source_handle,
            };
          });

          console.log(`Syncing edges to pipeline: ${updatedPipelineEdges.length} edges`);

          onPipelineChange({
            ...pipeline,
            edges: updatedPipelineEdges,
          });

          return currentEdges;
        });
      }, 0);
    },
    [onEdgesChange, pipeline, readonly, setEdges, onPipelineChange]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (readonly || !pipeline) return;

      const newEdge = {
        ...connection,
        id: `${connection.source}-${connection.target}${connection.sourceHandle ? `-${connection.sourceHandle}` : ''}`,
        type: 'movable',
        animated: true,
      };

      setEdges((eds) => addEdge(newEdge, eds));

      // Update pipeline
      const updatedPipeline = {
        ...pipeline,
        edges: [
          ...pipeline.edges,
          {
            id: newEdge.id,
            source: connection.source!,
            target: connection.target!,
            source_handle: connection.sourceHandle as 'true' | 'false' | null,
          },
        ],
      };
      onPipelineChange(updatedPipeline);
    },
    [readonly, pipeline, setEdges, onPipelineChange]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!pipeline || readonly) return;

      const taskData = event.dataTransfer.getData('application/reactflow');
      if (!taskData) return;

      try {
        const task: TaskTypeInfo = JSON.parse(taskData);

        // Use screenToFlowPosition to correctly handle zoom/pan
        const position = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        const isConditional = task.id === 'branch';
        const isItemSearch = task.id === 'item_search';
        const isReferenceGraphQuery = task.id === 'reference_graph_query';
        const isCreateReference = task.id === 'create_reference';
        const isPushback = task.id === 'pushback';
        const isCreateItem = task.id === 'create_item';
        const defaultConfig = isConditional
          ? { condition: undefined }
          : isItemSearch
          ? { search_kind: 'similarity', max_results: 5 }
          : isReferenceGraphQuery
          ? { search_kind: 'reference_graph', max_results: 5 }
          : isCreateReference
          ? { reference_type: 'REFERENCE', field_id: 'RQ_N/A', direction: 'item_to_related', cardinality: 'per_related_item' }
          : isPushback
          ? { target_field: '', operation: 'OVERRIDE', value_binding: { source: 'literal', literal_value: '' } }
          : isCreateItem
          ? {
              target_scope_id: '',
              target_project_id: '',
              target_type_id: '',
              field_mappings: DEFAULT_CREATE_ITEM_FIELD_MAPPINGS,
              cardinality: 'per_item',
              link_to_source: false,
              link_reference_type: 'ITEM_CREATED',
              link_field_id: 'RQ_N/A',
            }
          : {};

        const newNode = {
          id: `node-${Date.now()}`,
          task_type: task.id,
          label: task.label,
          position,
          config: defaultConfig,
        };

        // Add to React Flow
        const flowNode: Node = {
          id: newNode.id,
          type: isConditional ? 'conditionalNode' : 'taskNode',
          position,
          data: isConditional ? {
            taskType: task.id,
            label: task.label,
            condition: undefined,
          } : {
            taskType: task.id,
            label: task.label,
            config: defaultConfig,
          },
        };

        setNodes((nds) => [...nds, flowNode]);

        // Update pipeline
        onPipelineChange({
          ...pipeline,
          nodes: [...pipeline.nodes, newNode],
        });
      } catch (err) {
        console.error('Failed to drop node:', err);
      }
    },
    [pipeline, readonly, setNodes, onPipelineChange, reactFlowInstance]
  );

  const onNodeDragStop = useCallback(
    (_: any, node: Node) => {
      if (readonly || !pipeline) return;

      // Update pipeline with new position
      const updatedNodes = pipeline.nodes.map(n =>
        n.id === node.id ? { ...n, position: node.position } : n
      );

      onPipelineChange({
        ...pipeline,
        nodes: updatedNodes,
      });

      // Persist all node positions under reload-stable keys (see canvasLayout.ts)
      const layout = loadCanvasLayout(pipeline.id);
      layout.nodes = {};
      updatedNodes.forEach(n => {
        layout.nodes[stableNodeKey(n)] = n.position;
      });
      saveCanvasLayout(pipeline.id, layout);
    },
    [readonly, pipeline, onPipelineChange]
  );

  if (!pipeline) {
    return (
      <div className="pipeline-canvas-empty">
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="currentColor" opacity="0.3">
            <rect x="8" y="8" width="20" height="20" rx="2" />
            <rect x="36" y="8" width="20" height="20" rx="2" />
            <rect x="8" y="36" width="20" height="20" rx="2" />
            <rect x="36" y="36" width="20" height="20" rx="2" />
          </svg>
          <h3>No Pipeline Selected</h3>
          <p>Create a new pipeline or load an existing one to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pipeline-canvas">
      <EdgeRouteContext.Provider value={edgeRouteSetter}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeDragStop={onNodeDragStop}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        deleteKeyCode={readonly ? null : ['Backspace', 'Delete']}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls />
        <MiniMap
          nodeColor={() => '#0b2631'}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
        <Panel position="top-left">
          <div className="pipeline-info-card">
            <h3>{pipeline.metadata.name}</h3>
            {pipeline.metadata.description && (
              <p className="text-sm text-secondary">{pipeline.metadata.description}</p>
            )}
            <div className="pipeline-stats">
              <span className="badge badge-primary">{pipeline.nodes.length} nodes</span>
              <span className="badge badge-primary">{pipeline.edges.length} connections</span>
            </div>
          </div>
        </Panel>
      </ReactFlow>
      </EdgeRouteContext.Provider>
    </div>
  );
};

export const PipelineCanvas: React.FC<PipelineCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <FlowContent {...props} />
    </ReactFlowProvider>
  );
};
